import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Clock, Users, Award, BookOpen, Target, CheckCircle, GraduationCap } from 'lucide-react';

const MedCoursePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-purple-800 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/courses"
            className="inline-flex items-center text-purple-200 hover:text-white mb-6 transition-colors"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Courses
          </Link>
          <h1 className="text-4xl font-bold mb-4">M.Ed - Master of Education</h1>
          <p className="text-xl text-purple-100 max-w-3xl">
            A comprehensive 2-year postgraduate program designed to develop advanced teaching skills, educational research capabilities, and leadership qualities for aspiring education professionals.
          </p>
        </div>
      </div>

      {/* Course Details */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Overview</h2>
              <p className="text-gray-700 mb-4">
                Master of Education (M.Ed) is a postgraduate degree program that prepares educators for leadership roles in teaching, educational administration, curriculum development, and educational research. The program focuses on advanced pedagogical techniques, educational psychology, and contemporary issues in education.
              </p>
              <p className="text-gray-700">
                Students develop expertise in educational theory, research methodology, and practical teaching skills, enabling them to become effective educators and educational leaders who can contribute to improving the quality of education.
              </p>
            </div>

            {/* Course Structure */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Course Structure</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Duration & Structure</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      2 years (4 semesters)
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Semester-based curriculum
                    </li>
                    <li className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      Dissertation/project work
                    </li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Key Subjects</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• Educational Psychology</li>
                    <li>• Curriculum Development</li>
                    <li>• Educational Research</li>
                    <li>• Teaching Methodology</li>
                    <li>• Educational Administration</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Career Opportunities */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Career Opportunities</h2>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Teaching & Academia</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• College Lecturer</li>
                    <li>• Assistant Professor</li>
                    <li>• Educational Researcher</li>
                    <li>• Curriculum Developer</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Administration & Training</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• School Principal</li>
                    <li>• Educational Administrator</li>
                    <li>• Teacher Trainer</li>
                    <li>• Education Consultant</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Eligibility */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Eligibility Criteria</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Academic Requirements</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>• B.Ed or equivalent degree</li>
                    <li>• Minimum 50-55% aggregate marks</li>
                    <li>• Some universities require teaching experience</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Entrance Exams</h3>
                  <p className="text-gray-700">Universities may conduct entrance exams or consider merit-based admission</p>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Facts */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Facts</h3>
              <div className="space-y-3">
                <div className="flex items-center">
                  <Clock className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-gray-700">Duration: 2 Years</span>
                </div>
                <div className="flex items-center">
                  <Users className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-gray-700">Mode: Full-time</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-gray-700">Degree: M.Ed</span>
                </div>
                <div className="flex items-center">
                  <BookOpen className="h-5 w-5 text-purple-600 mr-3" />
                  <span className="text-gray-700">Research: Required</span>
                </div>
              </div>
            </div>

            {/* Specializations */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Popular Specializations</h3>
              <div className="space-y-3">
                <div className="border-l-4 border-purple-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Educational Technology</h4>
                  <p className="text-sm text-gray-600">Integration of technology in education</p>
                </div>
                <div className="border-l-4 border-blue-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Special Education</h4>
                  <p className="text-sm text-gray-600">Teaching students with special needs</p>
                </div>
                <div className="border-l-4 border-green-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Educational Administration</h4>
                  <p className="text-sm text-gray-600">School leadership and management</p>
                </div>
                <div className="border-l-4 border-red-500 pl-4">
                  <h4 className="font-semibold text-gray-900">Curriculum & Instruction</h4>
                  <p className="text-sm text-gray-600">Curriculum development and pedagogy</p>
                </div>
              </div>
            </div>

            {/* Skills Developed */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Skills Developed</h3>
              <div className="space-y-2">
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-700">Advanced Teaching Methods</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-700">Research & Analysis</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-700">Leadership & Management</span>
                </div>
                <div className="flex items-center">
                  <Target className="h-4 w-4 text-purple-600 mr-2" />
                  <span className="text-sm text-gray-700">Curriculum Design</span>
                </div>
              </div>
            </div>

            {/* Contact CTA */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-lg p-6 text-white">
              <h3 className="text-xl font-bold mb-3">Need Guidance?</h3>
              <p className="mb-4">Get expert counseling for M.Ed admissions</p>
              <Link
                to="/premium-services"
                className="inline-block bg-white text-purple-600 px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
              >
                Get Free Consultation
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedCoursePage;